import { useState, useEffect } from "react";
import { Clock, ChevronLeft, ChevronRight, Play, X, Calendar, Target, Zap } from "lucide-react";
import { fetchAuthSession } from "@aws-amplify/auth";
import axios from "axios"; 
import dayjs from "dayjs";
import RoundDownToFiveMinutes from "../../../components/utils/RoundTimeToFiveMinutes";
import { useNavigate } from "react-router-dom";

const CreateTrainingSession = ({ userId, defaultSlide = 2, onCancel }) => {
    const [stage, setStage] = useState('mood');
    const [currentMoodIndex, setCurrentMoodIndex] = useState(defaultSlide);
    const [isError, setIsError] = useState(false);

    const navigate = useNavigate();
    
    // Mock mood data - replace with your actual images
    const moods = [
        { emoji: "😢", label: "Very Bad", color: "from-red-500 to-red-600" },
        { emoji: "😕", label: "Bad", color: "from-orange-500 to-orange-600" },
        { emoji: "😐", label: "Neutral", color: "from-yellow-500 to-yellow-600" },
        { emoji: "😊", label: "Good", color: "from-green-500 to-green-600" },
        { emoji: "😄", label: "Very Good", color: "from-emerald-500 to-emerald-600" },
    ];

    const [sessionData, setSessionData] = useState({
        discipline: 'trampoline',
        sessionDate: new Date(),
        startTime: dayjs(RoundDownToFiveMinutes(new Date())),
        aims: '',
        fatigue: 5,  // Scale of 1-10
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchJwtToken = async () => {
            try {
                const session = await fetchAuthSession();
                const token = session.tokens?.idToken;
                axios.defaults.headers['Authorization'] = `Bearer ${token}`;
            } catch (error) {
                console.error('Error fetching JWT token:', error);
                setIsError(true);
            }
        };
        
        fetchJwtToken();
    }, []);


    const nextMood = () => {
        setCurrentMoodIndex(prev => (prev + 1) % moods.length);
    };

    const prevMood = () => {
        setCurrentMoodIndex(prev => (prev - 1 + moods.length) % moods.length);
    };

    const handleRecordMood = () => {
        setStage('confirmMood');
    };

    const handleConfirmMood = () => {
        if (currentMoodIndex < 2) {
            // For very bad or bad moods, show warning but still allow to continue
            setStage('sessionDetails');
        } else {
            setStage('sessionDetails');
        }
    };

    const handleFieldChange = (field, value) => {
        setSessionData(prev => ({ ...prev, [field]: value }));
    };

    const adjustTime = (minutes) => {
        setSessionData(prev => ({
            ...prev,
            startTime: prev.startTime.add(minutes, 'minute')
        }));
    };

    const formatTime = (dayjsTime) => {
        return dayjsTime.format('HH:mm');
    };

    const getFatigueLabel = (value) => {
        if (value === 0) return "No fatigue";
        if (value <= 2) return "Very low";
        if (value <= 4) return "Low";
        if (value <= 6) return "Moderate";
        if (value <= 8) return "High";
        return "Very high";
    };

    const getFatigueColor = (value) => {
        if (value <= 2) return "from-green-500 to-green-600";
        if (value <= 4) return "from-yellow-500 to-yellow-600";
        if (value <= 6) return "from-orange-500 to-orange-600";
        if (value <= 8) return "from-red-500 to-red-600";
        return "from-red-600 to-red-700";
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        
        const getCombinedDateTime = (date, time) => {
            return dayjs(date)
                .hour(time.hour())
                .minute(time.minute())
                .second(0)
                .millisecond(0);
        };

        const initialSession = {
            user_id: userId,
            session_date: getCombinedDateTime(sessionData.sessionDate, sessionData.startTime).format('YYYY-MM-DDTHH:mm:ssZ'),
            discipline: sessionData.discipline,
            session_aims: sessionData.aims,
            status: 'active',
            start_mood: currentMoodIndex,
            start_fatigue: sessionData.fatigue
        };

        // Simulate API call
        try {
            axios.post(process.env.REACT_APP_API_HOST + '/api/sessions', initialSession, {
                headers: {
                    'Content-Type': 'application/json', },
            })
            .then(response => {
            const session = response.data;
            navigate("/diary-entry", {state: {session: session}});
        })
            .catch(error => {
                console.error('Error posting session details:', error);
                setIsError(true);
            });  
        } catch (error) {
            console.error('Error creating session:', error);
            // Handle error appropriately
        } finally {
            setIsSubmitting(false);
        }
    };

    const getMoodMessage = (moodIndex) => {
        switch (moodIndex) {
            case 0:
            case 1:
                return "Before you start training, it's important to speak to your coach to make a plan to manage your mood.";
            case 2:
                return "Let your coach know you're feeling a bit meh. Maybe a great training session will make you smile!";
            case 3:
            case 4:
                return "It's great you're feeling happy and ready to go. Have a great session!";
            default:
                return "";
        }
    };

    const renderMoodSelection = () => (
        <>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-white transform rotate-12 translate-x-12"></div>
                </div>
                <div className="relative">
                    <h2 className="text-2xl font-bold text-center tracking-wide">How Are You Feeling Today?</h2>
                    <p className="p-style text-white text-center mt-1">Select your current mood</p>
                </div>
            </div>

            <div className="p-6">
                <div className="mb-8">
                    <div className="relative">
                        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden">
                            <div className={`absolute inset-0 bg-gradient-to-r ${moods[currentMoodIndex].color} opacity-10`}></div>
                            
                            <div className="relative text-center">
                                <div className="text-6xl mb-3 transform transition-all duration-300 hover:scale-110">
                                    {moods[currentMoodIndex].emoji}
                                </div>
                                <div className="text-xl font-semibold text-gray-800">
                                    {moods[currentMoodIndex].label}
                                </div>
                            </div>

                            <button
                                onClick={prevMood}
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all duration-200 hover:scale-110 cursor-pointer"
                            >
                                <ChevronLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            
                            <button
                                onClick={nextMood}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all duration-200 hover:scale-110 cursor-pointer"
                            >
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="flex justify-center space-x-2 mt-4">
                            {moods.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentMoodIndex(index)}
                                    className={`w-3 h-3 rounded-full transition-all duration-200 cursor-pointer ${
                                        index === currentMoodIndex 
                                            ? 'bg-indigo-500 scale-125' 
                                            : 'bg-gray-300 hover:bg-gray-400'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Fatigue Level */}
                <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                        <Zap className="w-5 h-5 mr-2 text-indigo-500" />
                        Current Fatigue
                    </h3>
                    <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                        <div className="text-center mb-4">
                            <div className={`inline-block text-sm px-4 py-2 rounded-lg text-white font-semibold bg-gradient-to-r ${getFatigueColor(sessionData.fatigue)}`}>
                                {sessionData.fatigue}/10 - {getFatigueLabel(sessionData.fatigue)}
                            </div>
                        </div>
                        <div className="relative">
                            <input
                                type="range"
                                min="0"
                                max="10"
                                step="1"
                                value={sessionData.fatigue}
                                onChange={(e) => handleFieldChange('fatigue', parseInt(e.target.value))}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                                style={{
                                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${sessionData.fatigue * 10}%, #e5e7eb ${sessionData.fatigue * 10}%, #e5e7eb 100%)`
                                }}
                            />
                            <div className="flex justify-between text-sm text-gray-500 mt-2">
                                <span>No fatigue</span>
                                <span>Very high</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex space-x-4">
                    <button
                        onClick={handleRecordMood}
                        className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2 cursor-pointer"
                    >
                        <span className="text-lg">Record Mood</span>
                    </button>

                    <button
                        onClick={() => onCancel?.()}
                        className="px-6 py-4 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 cursor-pointer"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </>
    );

    const renderMoodConfirmation = () => (
        <>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-white transform rotate-12 translate-x-12"></div>
                </div>
                <div className="relative">
                    <h2 className="text-2xl font-bold text-center tracking-wide">Mood Confirmation</h2>
                    <p className="p-style text-indigo-100 text-center mt-1">Ready to start your session?</p>
                </div>
            </div>

            <div className="p-6">
                <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden mb-6">
                    <div className={`absolute inset-0 bg-gradient-to-r ${moods[currentMoodIndex].color} opacity-10`}></div>
                    
                    <div className="relative text-center">
                        <div className="text-6xl mb-4">
                            {moods[currentMoodIndex].emoji}
                        </div>
                        <div className="text-xl font-semibold text-gray-800 mb-4">
                            {moods[currentMoodIndex].label}
                        </div>
                        <p className="p-style text-gray-600 text-sm leading-relaxed">
                            {getMoodMessage(currentMoodIndex)}
                        </p>
                    </div>
                </div>

                <div className="flex space-x-4">
                    {currentMoodIndex < 2 ? (
                        <>
                            <button
                                onClick={handleConfirmMood}
                                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
                            >
                                <span className="text-lg">Good to go!</span>
                            </button>
                            <button
                                onClick={() => onCancel?.()}
                                className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
                            >
                                <span className="text-lg">Miss this one out</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={handleConfirmMood}
                                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
                            >
                                <span className="text-lg">Good to go!</span>
                            </button>
                            <button
                                onClick={() => setStage('mood')}
                                className="px-6 py-4 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );

    const renderSessionDetails = () => (
        <>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0 bg-white transform rotate-12 translate-x-12"></div>
                </div>
                <div className="relative">
                    <h2 className="text-2xl font-bold text-center tracking-wide">Session Details</h2>
                    <p className="p-style text-indigo-100 text-center mt-1">Set up your training session</p>
                </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Discipline */}
                <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                        Discipline
                    </h3>
                    <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                        <select
                            value={sessionData.discipline}
                            onChange={(e) => handleFieldChange('discipline', e.target.value)}
                            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white"
                            style={{fontSize: "var(--text-lg)", lineHeight: "var(--tw-leading, var(--text-lg--line-height))"}}
                        >
                            <option value="trampoline">Trampoline</option>
                            <option value="dmt">DMT</option>
                            <option value="tumbling">Tumbling</option>
                        </select>
                    </div>
                </div>

                {/* Session Date */}
                <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-indigo-500" />
                        Session Date
                    </h3>
                    <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                        <input
                            type="date"
                            value={sessionData.sessionDate.toISOString().split('T')[0]}
                            onChange={(e) => handleFieldChange('sessionDate', new Date(e.target.value))}
                            className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white text-center"
                            style={{fontSize: "var(--text-lg)", lineHeight: "var(--tw-leading, var(--text-lg--line-height))"}}
                        />
                    </div>
                </div>

                {/* Start Time */}
                <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                        <Clock className="w-5 h-5 mr-2 text-indigo-500" />
                        Start Time
                    </h3>
                    <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                        <div className="text-center">
                            <div className="text-3xl font-bold text-gray-800 mb-4">
                                {formatTime(sessionData.startTime)}
                            </div>
                            <div className="flex justify-center space-x-2">
                                <button
                                    onClick={() => adjustTime(-15)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all duration-200 hover:scale-105"
                                >
                                    <span className="text-lg">-15m</span>
                                </button>
                                <button
                                    onClick={() => adjustTime(-5)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                >
                                    <span className="text-lg">-5m</span>
                                </button>
                                <button
                                    onClick={() => adjustTime(5)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                >
                                    <span className="text-lg">+5m</span>
                                </button>
                                <button
                                    onClick={() => adjustTime(15)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                >
                                    <span className="text-lg">+15m</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Session Aims */}
                <div className="mb-8">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                        <Target className="w-5 h-5 mr-2 text-indigo-500" />
                        Session Aims
                    </h3>
                    <div className="bg-white text-lg rounded-xl shadow-lg border border-gray-100">
                        <textarea
                            value={sessionData.aims}
                            onChange={(e) => handleFieldChange('aims', e.target.value)}
                            placeholder="What do you want to achieve in this session?"
                            className="w-full h-24 p-4 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white"
                        />
                    </div>
                </div>

                <div className="flex space-x-4 sticky bottom-0 bg-gradient-to-t from-slate-50 pt-4">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Play className="w-5 h-5" />
                        <span className="text-lg">{isSubmitting ? 'Starting...' : 'Begin Training!'}</span>
                    </button>

                    <button
                        onClick={() => setStage('confirmMood')}
                        className="px-6 py-4 bg-gradient-to-r from-gray-400 to-gray-500 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </>
    );

    if (isError) return <h2>An error has occurred.</h2>

    return (
        <div className="fixed inset-0 bg-black-500 flex items-center justify-center p-4 z-50">
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
                {stage === 'mood' && renderMoodSelection()}
                {stage === 'confirmMood' && renderMoodConfirmation()}
                {stage === 'sessionDetails' && renderSessionDetails()}
            </div>
        </div>
    );
};

export default CreateTrainingSession;

// import { useState } from "react";
// import axios from 'axios';
// import { useNavigate } from 'react-router-dom';
// import dayjs from "dayjs";
// import Carousel from "./Carousel";
// import MoodConf from "./MoodConfirmation";
// import SessionDetails from "./SessionDetails";
// import '../../../style/Moodscreen.css'
// import ErrorMessage from "../../../components/ErrorMessage.js";

// const CreateTrainingSession = ({userId, defaultSlide}) => {

//     const [stage, setStage] = useState('mood');
//     const [slideIdx, setSlideIdx] = useState(defaultSlide);
//     const [isError, setIsError] = useState(false);

//     const navigate = useNavigate();   

//     const imgs = [
//         '../img/mood_s/very_bad_w.png',
//         '../img/mood_s/bad_w.png',
//         '../img/mood_s/neutral_w.png',
//         '../img/mood_s/good_w.png',
//         '../img/mood_s/very_good_w.png',
//     ];

//     const handleRecordMood = (newIdx) => {
//         setSlideIdx(newIdx);
//         setStage('confirmMood');
//     };

//     const getCombinedDateTime = (date, time) => {
//         const dateTime = dayjs(date)
//           .hour(time.hour())
//           .minute(time.minute())
//           .second(0)
//           .millisecond(0);
    
//         return dateTime;
//     };

//     const handleConfirmMood = () => {
//         setStage('sessionDetails');
//     };

//     const handleSessionDetails = (sessionFields) => {

//         var initialSession = {
//             user_id: userId,
//             session_date: getCombinedDateTime(sessionFields.sessionDate, sessionFields.startTime).format('YYYY-MM-DDTHH:mm:ssZ'),
//             discipline: sessionFields.discipline,
//             session_aims: sessionFields.aims,
//             status: 'active',
//             start_mood: slideIdx
//         };

//         axios.post(process.env.REACT_APP_API_HOST + '/api/sessions', initialSession, {
//             headers: {
//                 'Content-Type': 'application/json',
//             },
//         })
//         .then(response => {
//             const session = response.data;
//             navigate("/diary-entry", {state: {session: session}});
//         })
//         .catch(error => {
//             console.error('Error posting session details:', error);
//             setIsError(true);
//         });        
//     };
    
//     const handleClose = () => {
//         setStage('mood');
//     };
    
//     if (isError) return <ErrorMessage handleConfirm={() => setIsError(false)} />;

//     if (stage === 'mood') {
//         return <Carousel images={imgs} defaultSlide={slideIdx} onRecord={handleRecordMood} /> 
//     }
//     else if (stage === 'confirmMood') {
//         return <MoodConf moodVal={slideIdx} handleConfirm={handleConfirmMood} handleClose={handleClose} />
//     }
//     else if (stage === 'sessionDetails') {
//         return <SessionDetails handleSubmit={handleSessionDetails} handleClose={handleClose} />
//     }
    
//     return <></>
    

// };

// export default CreateTrainingSession;